import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 25b(q43)/25c(q44) 矩陣 row 1-4 對應 4 項支持性措施：
  //   row1 臨時及短期照顧 / row2 照顧者支持與訓練 / row3 家庭關懷訪視 / row4 經濟補助
  // q43 row_op: 1=不需要, 2=需要
  // q44 row_op: 1=已利用滿意, 2=已利用尚可, 3=已利用不滿意, 4=未利用
  // q45-q48 是「未利用某項措施的原因」，逐列對應 q44 的 4 列
  const q44Visible = '{q43.1} = 2 or {q43.2} = 2 or {q43.3} = 2 or {q43.4} = 2'
  // q33 (18.輪替照顧者) 選到家屬/其他親友/其他/沒有人才顯示後續 q34-q36；
  // 選 10-14 (照服員/機構/本/外籍看護/其他家人均攤)、16 (沒有人可輪替) 則跳過
  const q33HasFamily =
    '({q33} >= 1 and {q33} <= 9) or {q33} = 15 or {q33} = "other"'
  const visibleIfConditionsInit = convertVisibleIf({
    // 1. q1=3(訪員離開後，自行填寫) → 顯示 q2 研究參與同意書；其他情形隱藏
    q2: '{q1} = 3',
    // 2. q12=10(11.未領有手冊) → 跳至 q15，隱藏 q13/q14
    q13: '{q12} <> 10',
    q14: '{q12} <> 10',
    // 3. q16=1(沒有工作) → 跳至 q21，隱藏 q17-q20
    q17: '{q16} = 2',
    q18: '{q16} = 2',
    q19: '{q16} = 2',
    // 4. q19=1(沒有影響) → 跳至 q21；q20 受 q16 與 q19 雙重控制
    q20: '{q16} = 2 and {q19} = 2',
    // 5. q21=1(沒有工作) → 跳至 q25，隱藏 q22-q24
    q22: '{q21} = 2',
    q23: '{q21} = 2',
    q24: '{q21} = 2',
    // 6. q31=1(沒有看護) → 跳至 q33，隱藏 q32
    q32: '{q31} = 2',
    // 7. q33 選 10-14/16(非家屬類) → 跳至 q37，隱藏 q34-q36
    q34: q33HasFamily,
    q35: q33HasFamily,
    q36: q33HasFamily,
    // 8. q38=10(沒有需要改善項目) → 跳至 q41，隱藏 q39/q40
    q39: '{q38} <> 10',
    // 9. q39=10(沒有次要) → 跳至 q41，隱藏 q40
    q40: '{q38} <> 10 and {q39} <> 10',
    // ====== 25b/25c 矩陣連動 ======
    // q44 整題：q43 任一列選「需要」才顯示
    q44: q44Visible,
    // q44 各列：對應 q43 該列選「需要」才顯示
    'q44.row1': '{q43.1} = 2',
    'q44.row2': '{q43.2} = 2',
    'q44.row3': '{q43.3} = 2',
    'q44.row4': '{q43.4} = 2',
    // q45-q48 未利用原因：對應 q44 該列選「未利用」(=4)
    q45: '{q44.1} = 4',
    q46: '{q44.2} = 4',
    q47: '{q44.3} = 4',
    q48: '{q44.4} = 4',
    // 10. q49=5(皆不需要) → 跳至 q52，隱藏 q50/q51
    q50: '{q49} <> 5',
    // 11. q50=5(沒有次要) → 跳至 q52，隱藏 q51
    q51: '{q49} <> 5 and {q50} <> 5',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
